/**
 * Unified anonymous web-telemetry primitive.
 *
 * WHAT THIS FILE DOES:
 * - Owns the single web-client transport for `POST /api/analytics/events`.
 * - Maintains an in-memory queue; flushes on interval, `visibilitychange=hidden`,
 *   `pagehide`, or explicit `flush()`.
 * - Uses `fetch({ keepalive, credentials: 'omit' })` for normal flushes so 429
 *   responses can trip persisted backoff. Lifecycle flushes may use
 *   `navigator.sendBeacon` because unload delivery matters more than observing
 *   the response.
 * - Owns the single anonymous device-id strategy keyed by `localStorage.gist.did`.
 *
 * WHY THIS EXISTS:
 * - Collapses five per-surface helpers (docs/share/discover/reader/try) into one.
 * - Fixes the tab-close share-funnel loss (no sendBeacon/keepalive previously).
 * - Collapses three device-id strategies (`gist.docs.analytics.device_id`,
 *   `gist_session_device_id`, discover anon id) into one stable localStorage key.
 *
 * CONTRACTS (do not violate):
 * - Endpoint: `POST /api/analytics/events`.
 * - Body: `{ deviceId, userId?, events: [{ event, ts, metadata? }] }`.
 * - Anonymous path sends no `Authorization` and uses `Content-Type:
 *   text/plain;charset=UTF-8` to keep the request CORS-simple (no preflight).
 * - Batch splitter chunks at ≤ 60KB (Chrome `fetch keepalive` 64KB cap).
 *
 * TRANSPORT MATRIX:
 * - Window (web page): `fetch({ keepalive: true })`; `sendBeacon` only for
 *   visibility/pagehide flushes.
 * - This file is NOT loaded by the service worker. `sendBeacon` is absent in
 *   `ServiceWorkerGlobalScope`; extension code owns its own keepalive path.
 *
 * SIMPLER IS BETTER:
 * - No retry. Analytics is best-effort. 4xx/5xx → drop.
 * - No feature flags.
 *
 * @file packages/backend/src/client/web-telemetry.ts
 */

/**
 * Single storage key for the anonymous web device id.
 *
 * Exported so docs/share/discover/reader/try all look at the same slot and
 * automated tests can assert on it.
 */
export const GIST_DEVICE_ID_KEY = 'gist.did';

/**
 * Server endpoint for analytics ingest.
 */
const ANALYTICS_ENDPOINT = '/api/analytics/events';

/**
 * Cap on batch bytes sent in a single request. Below Chrome's 64KB `fetch
 * keepalive` body cap, with headroom for envelope overhead.
 */
const MAX_BATCH_BYTES = 60 * 1024;

/**
 * How often the background flush timer wakes when events are queued.
 * Per plan spec: "≥ 5s interval".
 */
const FLUSH_INTERVAL_MS = 5_000;

/**
 * localStorage slot for the rate-limit backoff deadline (epoch ms).
 *
 * Persisted so a backoff survives reloads: once the anon ingest endpoint
 * returns 429, every subsequent page load within the window would otherwise
 * re-POST and re-trigger a console `429`. Persisting the deadline keeps the
 * console quiet across reloads.
 */
const TELEMETRY_BACKOFF_KEY = 'gist.telemetry.backoffUntilMs';

/**
 * Fallback backoff when the server omits a usable `Retry-After`. Matches the
 * server's anonymous rate-limit window so we resume right as it resets.
 */
const DEFAULT_BACKOFF_MS = 5 * 60 * 1000;

/**
 * Upper bound on a single backoff so a bogus `Retry-After` can't mute
 * telemetry for an unreasonable stretch.
 */
const MAX_BACKOFF_MS = 60 * 60 * 1000;

/**
 * Shape of one queued event.
 *
 * `ts` is the client wall-clock ISO string captured at `track()` time (not at
 * flush time). Matches the server zod schema.
 */
interface QueuedEvent {
  event: string;
  ts: string;
  metadata?: Record<string, unknown>;
}

/**
 * In-memory queue. Events accumulate here until flush.
 */
const queue: QueuedEvent[] = [];

/**
 * Cached device id. Resolved lazily on first `track()` / `getAnonDeviceId()`.
 * Null means "not yet resolved"; empty string means resolution produced nothing
 * (shouldn't happen — we always synthesize).
 */
let cachedDeviceId: string | null = null;

/**
 * Fallback counter used when `crypto.randomUUID` and `crypto.getRandomValues`
 * are both unavailable. Keeps generated ids unique within the page lifetime.
 */
let fallbackCounter = 0;

/**
 * Tracks whether background flush machinery (visibility listeners + interval)
 * has been installed for this page. Idempotent — safe to call many times.
 */
let lifecycleInstalled = false;

/**
 * Handle to the active flush interval. Cleared when the queue empties.
 */
let flushIntervalHandle: ReturnType<typeof setInterval> | null = null;

/**
 * Epoch-ms deadline before which we drop events without sending. Set when the
 * server returns 429 so we stop hammering (and stop spamming the console with
 * repeated `POST /api/analytics/events 429`). Hydrated lazily from
 * localStorage so the backoff survives reloads.
 */
let backoffUntilMs = 0;
let backoffHydrated = false;

/**
 * Hydrates `backoffUntilMs` from localStorage on first use. Idempotent.
 */
function hydrateBackoff(): void {
  if (backoffHydrated) {
    return;
  }
  backoffHydrated = true;
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    const raw = storage.getItem(TELEMETRY_BACKOFF_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : 0;
    if (Number.isFinite(parsed) && parsed > 0) {
      backoffUntilMs = parsed;
    }
  } catch {
    // Read failed — treat as no active backoff.
  }
}

/**
 * Records a backoff deadline (and persists it) after a 429.
 */
function setBackoff(deadlineMs: number): void {
  backoffUntilMs = deadlineMs;
  backoffHydrated = true;
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(TELEMETRY_BACKOFF_KEY, String(deadlineMs));
  } catch {
    // Write failed — in-memory backoff still applies for this page.
  }
}

/**
 * True while a server-requested backoff window is active.
 */
function isBackingOff(): boolean {
  hydrateBackoff();
  return Date.now() < backoffUntilMs;
}

/**
 * Converts a `Retry-After` header (delta-seconds) into an absolute backoff
 * deadline. Falls back to `DEFAULT_BACKOFF_MS` and caps at `MAX_BACKOFF_MS`.
 */
function backoffDeadlineFromHeader(retryAfter: string | null): number {
  const seconds = retryAfter ? Number.parseInt(retryAfter, 10) : Number.NaN;
  const ms = Number.isFinite(seconds) && seconds > 0
    ? Math.min(seconds * 1000, MAX_BACKOFF_MS)
    : DEFAULT_BACKOFF_MS;
  return Date.now() + ms;
}

/**
 * Generates a new anonymous device id.
 *
 * Priority per plan:
 * 1. `crypto.randomUUID()` — modern browsers.
 * 2. `crypto.getRandomValues(Uint8Array(16))` → hex — older browsers with
 *    Web Crypto but no `randomUUID`.
 * 3. `Date.now()` + module-scoped counter — last-resort fallback.
 *
 * @returns Freshly minted device id string.
 */
function generateDeviceId(): string {
  const cryptoSource: Crypto | undefined = typeof globalThis !== 'undefined'
    ? (globalThis as { crypto?: Crypto }).crypto
    : undefined;

  if (cryptoSource && typeof cryptoSource.randomUUID === 'function') {
    try {
      return cryptoSource.randomUUID();
    } catch {
      // Some locked-down environments throw from randomUUID; fall through.
    }
  }

  if (cryptoSource && typeof cryptoSource.getRandomValues === 'function') {
    try {
      const bytes = new Uint8Array(16);
      cryptoSource.getRandomValues(bytes);
      let hex = '';
      for (const byte of bytes) {
        hex += byte.toString(16).padStart(2, '0');
      }
      return hex;
    } catch {
      // Fall through to counter fallback.
    }
  }

  // Last-resort path (no Web Crypto at all). Pad with Math.random entropy so the
  // id always clears the server's 16-char minimum and never 400s as
  // invalid_device_id.
  fallbackCounter += 1;
  const pad = Math.random().toString(36).slice(2, 12).padEnd(10, '0');
  return `gist_${Date.now().toString(36)}_${fallbackCounter.toString(36)}_${pad}`;
}

/**
 * Returns the current page's `localStorage` when available.
 *
 * Returns null on SSR, when `window` is absent, or when `localStorage`
 * access throws (disabled storage, sandboxed iframes, quota denied, etc.).
 */
function getLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    const storage = window.localStorage;
    if (!storage) {
      return null;
    }
    // Touching the storage to confirm access doesn't throw.
    const probeKey = '__gist_did_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return storage;
  } catch {
    return null;
  }
}

/**
 * Resolves (or creates) the anonymous device id for this browser.
 *
 * Persists to `localStorage[GIST_DEVICE_ID_KEY]` when available. When
 * storage is blocked, falls back to an in-memory id for the page lifetime —
 * do not silently no-op.
 */
export function getAnonDeviceId(): string {
  if (cachedDeviceId) {
    return cachedDeviceId;
  }

  const storage = getLocalStorage();

  if (storage) {
    try {
      const stored = storage.getItem(GIST_DEVICE_ID_KEY);
      if (stored && stored.trim().length > 0) {
        cachedDeviceId = stored.trim();
        return cachedDeviceId;
      }
    } catch {
      // Read failed — fall through to generate.
    }

    const next = generateDeviceId();
    try {
      storage.setItem(GIST_DEVICE_ID_KEY, next);
    } catch {
      // Write failed — still return the generated id for this page.
    }
    cachedDeviceId = next;
    return next;
  }

  cachedDeviceId = generateDeviceId();
  return cachedDeviceId;
}

/**
 * Splits a list of events into chunks whose serialized JSON body stays
 * under `MAX_BATCH_BYTES`. Ensures compliance with Chrome's 64KB
 * `fetch keepalive` cap and parallel anti-abuse checks on the server.
 *
 * Single events that exceed the limit are still emitted as a lone chunk —
 * the server will reject them. That surfaces the bug at the caller rather
 * than silently dropping on the client.
 */
function splitBatch(events: QueuedEvent[]): QueuedEvent[][] {
  const chunks: QueuedEvent[][] = [];
  let current: QueuedEvent[] = [];
  let currentBytes = 2; // `[]`

  for (const event of events) {
    const encoded = JSON.stringify(event);
    const encodedBytes = encoded.length + 1; // `,` or leading `[`
    if (current.length > 0 && currentBytes + encodedBytes > MAX_BATCH_BYTES) {
      chunks.push(current);
      current = [];
      currentBytes = 2;
    }
    current.push(event);
    currentBytes += encodedBytes;
  }
  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
}

/**
 * Sends one chunk of events. Best-effort — on failure we `console.warn` and
 * drop (no retry). Never throws.
 *
 * Transport:
 * - Normal flush: `fetch({ keepalive: true, credentials: 'omit' })` so we can
 *   observe 429 and persist backoff.
 * - Lifecycle flush: optional `navigator.sendBeacon` first; it cannot observe
 *   429, but is more reliable while the page is unloading.
 */
function sendChunk(events: QueuedEvent[], preferBeacon = false): void {
  if (events.length === 0) {
    return;
  }

  let deviceId: string;
  try {
    deviceId = getAnonDeviceId();
  } catch {
    console.warn('[web-telemetry] Failed to resolve device id; dropping batch.');
    return;
  }

  const body = JSON.stringify({
    deviceId,
    events
  });

  try {
    if (preferBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(ANALYTICS_ENDPOINT, blob)) {
        return;
      }
    }
  } catch {
    // Some browsers throw when sendBeacon is passed a blob they don't accept;
    // fall through to fetch.
  }

  try {
    if (typeof fetch !== 'function') {
      console.warn('[web-telemetry] No fetch available; dropping batch.');
      return;
    }
    void fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body
    }).then((response) => {
      if (response && response.status === 429) {
        setBackoff(backoffDeadlineFromHeader(response.headers.get('Retry-After')));
      }
    }).catch(() => {
      // Best-effort: swallow rejections.
    });
  } catch {
    console.warn('[web-telemetry] fetch transport threw; dropping batch.');
  }
}

/**
 * Installs visibility + pagehide + interval listeners so the queue drains
 * reliably around navigation. Idempotent; safe to call many times.
 *
 * Guarded by `lifecycleInstalled` so we don't stack listeners if callers
 * import the primitive across code-split chunks.
 */
function installLifecycleHooks(): void {
  if (lifecycleInstalled) {
    return;
  }
  lifecycleInstalled = true;

  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush(true);
      }
    });
  }

  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', () => {
      flush(true);
    });
  }
}

/**
 * Ensures the background flush interval is running while the queue is
 * non-empty. Cleared in `flush()` when the queue drains to keep the page
 * idle when nothing is pending.
 */
function ensureFlushInterval(): void {
  if (flushIntervalHandle !== null) {
    return;
  }
  if (typeof setInterval !== 'function') {
    return;
  }
  flushIntervalHandle = setInterval(() => {
    flush();
  }, FLUSH_INTERVAL_MS);
}

function clearFlushInterval(): void {
  if (flushIntervalHandle === null) {
    return;
  }
  if (typeof clearInterval === 'function') {
    clearInterval(flushIntervalHandle);
  }
  flushIntervalHandle = null;
}

/**
 * Queues one event for batched delivery.
 *
 * Event name plus optional properties. `ts` is captured now (ISO string) so
 * late flushes don't drift the timestamp.
 *
 * Safe during SSR: no-op when `navigator` and `fetch` are both unavailable.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  if (typeof event !== 'string' || event.trim().length === 0) {
    return;
  }
  if (typeof navigator === 'undefined' && typeof fetch !== 'function') {
    return;
  }

  const entry: QueuedEvent = {
    event: event.trim(),
    ts: new Date().toISOString()
  };
  if (props && typeof props === 'object') {
    entry.metadata = props;
  }

  queue.push(entry);

  installLifecycleHooks();
  ensureFlushInterval();
}

/**
 * Drains the queue synchronously, sending each ≤ 60KB chunk.
 *
 * Callers:
 * - Internal: visibilitychange/pagehide/interval.
 * - External: call sites that want to force-drain before navigation
 *   (e.g. share CTA -> navigate away).
 */
export function flush(preferBeacon = false): void {
  if (queue.length === 0) {
    clearFlushInterval();
    return;
  }

  if (isBackingOff()) {
    // Server asked us to slow down. Best-effort analytics: drop the queued
    // events without touching the network so we don't re-trigger the 429 the
    // browser would log to the console.
    queue.length = 0;
    clearFlushInterval();
    return;
  }

  const batch = queue.splice(0, queue.length);
  const chunks = splitBatch(batch);
  for (const chunk of chunks) {
    sendChunk(chunk, preferBeacon);
  }

  if (queue.length === 0) {
    clearFlushInterval();
  }
}

/**
 * Test-only helper to reset module state between tests.
 *
 * Not exported from the public API surface callers use, but exported for the
 * unit tests in this file's sibling test. Not documented for product code.
 */
export function __resetForTests(): void {
  queue.length = 0;
  cachedDeviceId = null;
  fallbackCounter = 0;
  lifecycleInstalled = false;
  backoffUntilMs = 0;
  backoffHydrated = false;
  clearFlushInterval();
}
